import React, { useEffect, useCallback, useState } from 'react';
import { NativeSelect, FormControl, InputLabel, Box, Skeleton } from '@mui/material'
import { DropdownEntry } from '../types';
import { useDropdownEntries } from '../hooks';
import { DropdownIdentifier } from '../constants';

type Props<TEntry> = {
  label: string
  defaultValue?: string
  dropdownIdentifier: DropdownIdentifier
  onChange: (value: TEntry | null) => void
}


function DropdownSelector<TEntry extends DropdownEntry>(props: Props<TEntry>) {
  const {data: entries = [], isLoading, error } = useDropdownEntries<TEntry>(
    props.dropdownIdentifier);
  const [selectedEntry, setSelectedEntry] = useState<string>('');

  if (error) {
    console.error('Error fetching dropdown entries:', error);
  }

  const changeCallback = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const entry = entries.find(entry => entry.value === event.target.value);
    setSelectedEntry(event.target.value);
    props.onChange(entry || null);
  }, [props, entries]);

  useEffect(() => {
    if (entries.length && !selectedEntry) {
      const initialEntry = props.defaultValue
        ? entries.find(entry => entry.value === props.defaultValue) || entries[0]
        : entries[0];
      setSelectedEntry(initialEntry.value);
      props.onChange(initialEntry);
    }
  }, [entries, props])

  if (isLoading || error) {
    return (
      <FormControl fullWidth>
        <InputLabel variant="standard" shrink>
          {props.label}
        </InputLabel>
        <Box sx={{ mt: 2 }}>
          <Skeleton height={32} data-testid="skeleton" />
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" htmlFor={`${props.label}-select`}>
        {props.label}
      </InputLabel>
      <NativeSelect
        inputProps={{
          id: `${props.label}-select`,
          "data-cy": props.label
        } as React.SelectHTMLAttributes<HTMLSelectElement>}
        value={selectedEntry}
        onChange={changeCallback}
      >
        {entries.map((entry) => (
          <option key={entry.value} value={entry.value}>{entry.name}</option>
        ))}
      </NativeSelect>
    </FormControl>
  )
}

export default DropdownSelector;
